// The dashboard's browser-location lifecycle (W47.1 follow-up).
//
// Split out of `routes/Dashboard.tsx` for two reasons, one of them a hard rule:
//
// 1. **`AGENTS.md §3 #10`** — route components are capped at 15 KB, and the state,
//    the callback and the reasoning about them pushed the dashboard to 16.1 KB.
//    The ceiling exists because a route that grows is a route nobody splits.
// 2. **It was the right seam anyway.** A route should not own a permission
//    lifecycle: the prompt fires, the answer arrives later, the screen may be
//    gone by then, and the user may ask again from a card in the middle of the
//    page. None of that is about the dashboard's layout, so none of it belongs in
//    the file that lays the page out.
//
// **It takes no callback, and that is the point.** An earlier version accepted
// `onGranted` and refetched on a fresh fix — which both duplicated the fetch and
// created a cycle, because the route would have had to pass the `refetch` that
// comes *from* the query this hook's coordinates feed. `useDashboard`'s key
// already carries the coordinates, so a grant produces a new key and React Query
// fetches on its own. One mechanism, in one place.
//
// Two states that look the same to the card and mean opposite things:
// `requesting` is "we asked and are waiting"; the absence of coordinates is "we
// were never given any". Collapsing them would put the card in its loading state
// on every first paint.

import { useCallback, useEffect, useState } from "react";

import {
  requestCoordinates,
  resolveCoordinates,
  type GeoReason,
} from "@/lib/weather/geolocation";

export interface WeatherLocation {
  /** Fed to `useDashboard` — `null` on either side means 「fallback 香港」. */
  coords: { lat: number | null; lon: number | null };
  /**
   * W47.7 — why there are no coordinates, carried all the way to the card.
   *
   * `unasked` is the only one the card has no copy for, and that is deliberate:
   * it is the first paint, before anybody was asked, and the card has not failed
   * at anything yet. Every state *after* a press says what happened.
   */
  reason: GeoReason;
  /** True while a second request is in flight, so the card can say so. */
  requesting: boolean;
  /** Ask the browser again, bypassing the cache. Never throws. */
  ask: () => Promise<void>;
}

export function useWeatherLocation(): WeatherLocation {
  const [coords, setCoords] = useState<{ lat: number | null; lon: number | null }>({
    lat: null,
    lon: null,
  });
  const [reason, setReason] = useState<GeoReason>("unasked");
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    let live = true;
    void resolveCoordinates().then((resolved) => {
      // The screen may be unmounted before the browser answers — writing state
      // after that is the one crash this call site could produce.
      if (live) {
        setCoords({ lat: resolved.lat, lon: resolved.lon });
        setReason(resolved.reason);
      }
    });
    return () => {
      live = false;
    };
  }, []);

  const ask = useCallback(async () => {
    setRequesting(true);
    try {
      // `resolveCoordinates` would return the cached `{null, null}` written when
      // the first prompt was dismissed, which would make this press a no-op that
      // looks like it did something. Someone pressing 「重新授權」 is asking the
      // browser again, so the cache has to be out of the way.
      const resolved = await requestCoordinates();
      // Re-writing `{null, null}` is a no-op for the query key — the weather
      // does not change — but the **reason** does, and that is the whole point of
      // W47.7: the card now says whether the browser refused, could not find a
      // position, or ran out of time, instead of showing the same badge it was
      // showing before the press.
      setCoords({ lat: resolved.lat, lon: resolved.lon });
      setReason(resolved.reason);
    } finally {
      setRequesting(false);
    }
  }, []);

  return { coords, reason, requesting, ask };
}